import { LogLevel } from '@lender/utils';

import { IS_LOCAL_DEVELOPMENT } from 'constants/envs';
import { HTTP_METHOD } from 'constants/http';
import { jsonStringify } from 'utils/json';

import RequestDeduplicator from './requestDeduplicator';

class ApiClient {
  static #isDeduplicationEnabled = false;

  #client;
  #logger;
  #errorLogLevel;
  #deduplicator;

  constructor(client, logger, settings = {}) {
    this.#client = client;
    this.#logger = logger;

    const { errorLogLevel, deduplicationOptions = {} } = settings;

    this.#errorLogLevel = errorLogLevel;

    this.#deduplicator = new RequestDeduplicator({ ...deduplicationOptions, logger });
  }

  static setIsDeduplicationEnabled(value) {
    ApiClient.#isDeduplicationEnabled = value;
  }

  async request(config) {
    if (IS_LOCAL_DEVELOPMENT && ApiClient.#isDeduplicationEnabled) {
      if (this.#deduplicator.hasPendingRequest(config)) {
        return this.#deduplicator.getPendingRequest(config);
      }

      const promise = this.#client.request(config).catch(error => {
        this.#deduplicator.removePendingRequestByConfig(config);

        return this.#handleServerError(error);
      });

      this.#deduplicator.addPendingRequest(config, promise);

      return promise;
    }

    return this.#client.request(config).catch(this.#handleServerError.bind(this));
  }

  async get(url, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.GET });
  }

  async delete(url, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.DELETE });
  }

  async head(url, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.HEAD });
  }

  async options(url, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.OPTIONS });
  }

  async post(url, body, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.POST, data: body });
  }

  async put(url, body, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.PUT, data: body });
  }

  async patch(url, body, config) {
    return this.request({ ...config, url, method: HTTP_METHOD.PATCH, data: body });
  }

  async injectRequestInterceptor(onFulfilled, onRejected) {
    return this.#client.interceptors.request.use(onFulfilled, onRejected);
  }

  ejectRequestInterceptor(interceptor) {
    return this.#client.interceptors.request.eject(interceptor);
  }

  async injectResponseInterceptor(onFulfilled, onRejected) {
    return this.#client.interceptors.response.use(onFulfilled, onRejected);
  }

  ejectResponseInterceptor(interceptor) {
    return this.#client.interceptors.response.eject(interceptor);
  }

  // dead switch code
  #handleServerError(error) {
    switch (this.#errorLogLevel) {
      case LogLevel.ERROR:
        this.#logger.error('RequestError', jsonStringify(error));
        break;

      case LogLevel.WARN:
        this.#logger.warn('RequestError', jsonStringify(error));
        break;

      default:
        break;
    }

    return Promise.reject(error);
  }
}

export default ApiClient;
